import React, { useState, useEffect } from "react";
import {
  Col,
  Row,
  Table,
  Button,
  Badge,
  Input,
  InputGroup,
  InputGroupAddon,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import Widget from "../../components/Widget/Widget.js";
import { getInventory, updateStock, addItem, updateItem } from "../../services/inventoryService.js";

import searchIcon from "../../assets/tables/searchIcon.svg";
import s from "./Inventory.module.scss";

const Inventory = function () {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    current_stock: '',
    min_stock: '',
    max_stock: '',
    unit: '',
    location: '',
    supplier: '',
    barcode: ''
  });

  // Load inventory data on component mount
  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const data = await getInventory();
      setInventory(data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    }
    setLoading(false);
  };

  // Filter inventory based on search and category
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "All" || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter dropdown
  const categories = ["All", ...new Set(inventory.map(item => item.category).filter(Boolean))];

  // Determine stock status
  const getStockStatus = (current, min) => {
    if (current <= min) return { text: "Critical", color: "danger" };
    if (current <= min * 1.5) return { text: "Low", color: "warning" };
    return { text: "Good", color: "success" };
  };

  // Form handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      item_name: '',
      category: '',
      current_stock: '',
      min_stock: '',
      max_stock: '',
      unit: '',
      location: '',
      supplier: '',
      barcode: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openUpdateModal = (item) => {
    setCurrentItem(item);
    setFormData({
      item_name: item.item_name || '',
      category: item.category || '',
      current_stock: item.current_stock || '',
      min_stock: item.min_stock || '',
      max_stock: item.max_stock || '',
      unit: item.unit || '',
      location: item.location || '',
      supplier: item.supplier || '',
      barcode: item.barcode || ''
    });
    setShowUpdateModal(true);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await addItem(formData);
      if (result.success) {
        setShowAddModal(false);
        resetForm();
        loadInventory();
      } else {
        alert('Failed to add item: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to add item: ' + error.message);
    }
    setLoading(false);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await updateItem(currentItem.id, formData);
      if (result.success) {
        setShowUpdateModal(false);
        setCurrentItem(null);
        resetForm();
        loadInventory();
      } else {
        alert('Failed to update item: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to update item: ' + error.message);
    }
    setLoading(false);
  };

  const handleStockUpdate = async (id, newStock) => {
    try {
      await updateStock(id, newStock);
      // Reload inventory to get updated data
      loadInventory();
    } catch (error) {
      console.error('Failed to update stock:', error);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
        <div>Loading inventory...</div>
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <Widget>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="headline-2">Hotel Inventory Management</div>
              <div className="d-flex align-items-center">
                <InputGroup className="mr-3" style={{ width: "250px" }}>
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <InputGroupAddon addonType="append">
                    <img src={searchIcon} alt="Search" style={{ padding: "8px" }} />
                  </InputGroupAddon>
                </InputGroup>
                
                <UncontrolledDropdown className="mr-3">
                  <DropdownToggle caret>
                    {categoryFilter}
                  </DropdownToggle>
                  <DropdownMenu>
                    {categories.map(category => (
                      <DropdownItem 
                        key={category}
                        onClick={() => setCategoryFilter(category)}
                      >
                        {category}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
                
                <Button color="success" onClick={openAddModal}>
                  Add Item
                </Button>
              </div>
            </div>

            <Table responsive striped>
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Current Stock</th>
                  <th>Min Stock</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Supplier</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      {searchTerm || categoryFilter !== "All" ? "No items found matching filters" : "No inventory items found"}
                    </td>
                  </tr>
                ) : (
                  filteredInventory.map((item) => {
                    const stockStatus = getStockStatus(item.current_stock, item.min_stock);
                    return (
                      <tr key={item.id}>
                        <td className="font-weight-bold">{item.item_name}</td>
                        <td>{item.category}</td>
                        <td className="font-weight-bold">{item.current_stock} {item.unit}</td>
                        <td>{item.min_stock}</td>
                        <td>
                          <Badge color={stockStatus.color}>
                            {stockStatus.text}
                          </Badge>
                        </td>
                        <td>{item.location}</td>
                        <td>{item.supplier}</td>
                        <td>
                          <Button 
                            size="sm" 
                            color="primary" 
                            outline
                            className="mr-2"
                            onClick={() => openUpdateModal(item)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            color="warning" 
                            outline
                            onClick={() => {
                              const newStock = prompt(`Update stock for ${item.item_name}:`, item.current_stock);
                              if (newStock !== null && !isNaN(newStock)) {
                                handleStockUpdate(item.id, parseInt(newStock));
                              }
                            }}
                          >
                            Stock
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>

            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {filteredInventory.length} of {inventory.length} items
              </div>
              <Button color="success" onClick={loadInventory}>
                Refresh Data
              </Button>
            </div>
          </Widget>
        </Col>
      </Row>

      {/* Add Item Modal */}
      <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)} size="lg">
        <ModalHeader toggle={() => setShowAddModal(false)}>Add New Item</ModalHeader>
        <Form onSubmit={handleAddItem}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="item_name">Item Name *</Label>
                  <Input
                    type="text"
                    name="item_name"
                    id="item_name"
                    value={formData.item_name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="category">Category *</Label>
                  <Input
                    type="text"
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="current_stock">Current Stock *</Label>
                  <Input
                    type="number"
                    name="current_stock"
                    id="current_stock"
                    value={formData.current_stock}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="min_stock">Min Stock</Label>
                  <Input
                    type="number"
                    name="min_stock"
                    id="min_stock"
                    value={formData.min_stock}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="max_stock">Max Stock</Label>
                  <Input
                    type="number"
                    name="max_stock"
                    id="max_stock"
                    value={formData.max_stock}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="unit">Unit</Label>
                  <Input
                    type="text"
                    name="unit"
                    id="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder="pieces, bottles, sets"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="location">Location</Label>
                  <Input
                    type="text"
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="supplier">Supplier</Label>
                  <Input
                    type="text"
                    name="supplier"
                    id="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="barcode">Barcode</Label>
                  <Input
                    type="text"
                    name="barcode"
                    id="barcode"
                    value={formData.barcode}
                    onChange={handleInputChange}
                    placeholder="Scan or enter barcode number"
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Item'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Update Item Modal */}
      <Modal isOpen={showUpdateModal} toggle={() => setShowUpdateModal(false)} size="lg">
        <ModalHeader toggle={() => setShowUpdateModal(false)}>
          Update Item: {currentItem?.item_name}
        </ModalHeader>
        <Form onSubmit={handleUpdateItem}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_item_name">Item Name *</Label>
                  <Input
                    type="text"
                    name="item_name"
                    id="update_item_name"
                    value={formData.item_name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="update_category">Category *</Label>
                  <Input
                    type="text"
                    name="category"
                    id="update_category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_current_stock">Current Stock *</Label>
                  <Input
                    type="number"
                    name="current_stock"
                    id="update_current_stock"
                    value={formData.current_stock}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_min_stock">Min Stock</Label>
                  <Input
                    type="number"
                    name="min_stock"
                    id="update_min_stock"
                    value={formData.min_stock}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_max_stock">Max Stock</Label>
                  <Input
                    type="number"
                    name="max_stock"
                    id="update_max_stock"
                    value={formData.max_stock}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_unit">Unit</Label>
                  <Input
                    type="text"
                    name="unit"
                    id="update_unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_location">Location</Label>
                  <Input
                    type="text"
                    name="location"
                    id="update_location"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="update_supplier">Supplier</Label>
                  <Input
                    type="text"
                    name="supplier"
                    id="update_supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="update_barcode">Barcode</Label>
                  <Input
                    type="text"
                    name="barcode"
                    id="update_barcode"
                    value={formData.barcode}
                    onChange={handleInputChange}
                    placeholder="Scan or enter barcode number"
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowUpdateModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Item'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default Inventory;